/**
 * Cross-component signal that the org's game catalogue changed (a game was registered, or a build
 * added/edited) so the header context/game switcher re-fetches its game lists instead of showing a
 * stale set. The header listens for {@link GAMES_CHANGED_EVENT}; mutators call {@link notifyGamesChanged}
 * after a successful save. Mirrors the lifecycle-changed event pattern in useSelectedGameLifecycle.
 */
export const GAMES_CHANGED_EVENT = 'gp-games-changed';

/** Fire-and-forget: tell mounted listeners (the header switcher) to refresh their game lists. */
export function notifyGamesChanged() {
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event(GAMES_CHANGED_EVENT));
    }
}

/**
 * The server's game-open refusal (MDCWebContextFilter: 403 with a code), or null for any other failure:
 * 'suspended' when an owner suspended the caller on that game, 'denied' when they have no access to it.
 * Either means this tab's view of the caller's games is stale - e.g. they were suspended mid-session.
 */
export function gameAccessRefusal(error) {
    if (error?.response?.status !== 403) return null;
    const code = error.response.data?.code;
    if (code === 'GAME_ACCESS_SUSPENDED') return 'suspended';
    if (code === 'GAME_ACCESS_DENIED') return 'denied';
    return null;
}

/*
 * Games the server refused to open for the signed-in user. The selected game rides on every request as the
 * default X-Web-Game-Id, and the server refuses ANY request naming a game the caller can't open. So once a
 * user was suspended on their selected game, everything failed - their profile, notifications, the org name
 * and the game list the header needs to move them to another game - and they were left on a blank page
 * (reported: a user suspended on one of two games could not use the other). A refused game is no longer sent
 * as the DEFAULT; a request that names it explicitly (that game's own pages) still gets the refusal and its
 * card. Keyed by user, so a different sign-in in the same tab starts clean; cleared when access changes.
 */
const refusedGames = new Set();
const refusalKey = (userKey, gameId) => `${userKey || ''}|${gameId}`;

/** The game id the refused request named, or null. Reads axios's header bag case-insensitively. */
export function refusedGameOf(error) {
    if (!gameAccessRefusal(error)) return null;
    const headers = error?.config?.headers || {};
    const name = Object.keys(headers).find(k => k.toLowerCase() === 'x-web-game-id');
    return (name && headers[name]) || null;
}

/** Remember that the server refused `gameId` for `userKey` (the signed-in user). */
export function rememberRefusedGame(userKey, gameId) {
    if (gameId) refusedGames.add(refusalKey(userKey, gameId));
}

/** Forget every remembered refusal, e.g. when the caller's access changed. */
export function forgetRefusedGames() {
    refusedGames.clear();
}

/** The game to send as the default X-Web-Game-Id: the selected one, unless the server refused it for this user. */
export function defaultGameHeader(userKey, selectedGameId) {
    if (!selectedGameId) return null;
    return refusedGames.has(refusalKey(userKey, selectedGameId)) ? null : selectedGameId;
}

const REFUSAL_REFRESH_GAP_MS = 5000;
let lastRefusalRefresh = 0;

/**
 * Re-reads the caller's games after a game-open refusal, at most once per few seconds: a page fires many
 * requests and each is refused, but one refresh is enough to swap the page for "your access is suspended".
 */
export function notifyGameAccessRefused(now = Date.now()) {
    if (now - lastRefusalRefresh < REFUSAL_REFRESH_GAP_MS) return false;
    lastRefusalRefresh = now;
    notifyGamesChanged();
    return true;
}
